import cx from 'classnames';
import { useCallback, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dynamicToken } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';

import { LazyInviteToJiraModal } from './LazyInviteToJiraModal';

import * as styles from './InviteToJiraButton.module.less';

interface InviteToJiraButtonProps {
  workspaceId?: string;
  isNewWorkspacePage?: boolean;
}

export const InviteToJiraButton: FunctionComponent<InviteToJiraButtonProps> = ({
  workspaceId,
  isNewWorkspacePage = false,
}) => {
  const {
    show: showDialog,
    isOpen: isDialogOpen,
    hide: closeDialog,
    dialogProps,
  } = useDialog();

  const onClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'openInviteToJiraButton',
      source: 'currentWorkspaceNavigationDrawer',
    });
    showDialog();
  }, [showDialog]);

  if (!workspaceId) {
    return;
  }

  return (
    <>
      <Button
        onClick={onClick}
        className={cx(
          styles.workspaceLink,
          isNewWorkspacePage && styles.newWorkspacePage,
        )}
        shouldFitContainer
      >
        <div className={styles.shortcutIconFlagEnabled}>
          <AddMemberIcon
            label=""
            size="small"
            color={dynamicToken('dynamic.icon')}
          />
        </div>
        <p className={styles.workspaceLinkText}>
          <FormattedMessage
            defaultMessage="Invite to Jira"
            id="templates.invite_to_jira.invite-to-jira"
            description="Text for a button that opens a modal to invite members to a linked Jira Workspace"
          />
        </p>
      </Button>

      {isDialogOpen && (
        <Dialog {...dialogProps}>
          <LazyInviteToJiraModal
            workspaceId={workspaceId}
            closeDialog={closeDialog}
          />
        </Dialog>
      )}
    </>
  );
};
